import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { MessageCircle, Mail, ArrowLeft, CheckCircle2, Loader2, Clock } from 'lucide-react';

export default function OTPTestPage() {
  const navigate = useNavigate();
  
  // Step 1: Phone input
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Step 2: OTP verification
  const [otpSent, setOtpSent] = useState(false);
  const [channel, setChannel] = useState('');
  const [onWhatsApp, setOnWhatsApp] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // SMS Fallback
  const [countdown, setCountdown] = useState(35);
  const [canShowFallback, setCanShowFallback] = useState(false);
  const [sendingFallback, setSendingFallback] = useState(false);

  // Countdown timer for SMS fallback
  useEffect(() => {
    if (otpSent && channel === 'whatsapp' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanShowFallback(true);
    }
  }, [otpSent, channel, countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    const cleaned = phone.replace(/\s/g, '');
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const response = await publicApi.sendOTPSmart('+91' + cleaned);
      const data = response.data;
      
      setOtpSent(true);
      setChannel(data.channel);
      setOnWhatsApp(data.on_whatsapp);
      setCountdown(35);
      setCanShowFallback(false);
      
      if (data.channel === 'whatsapp') {
        toast.success('🎉 OTP sent to your WhatsApp!', {
          description: 'Check your WhatsApp for the OTP code'
        });
      } else {
        toast.success('📱 OTP sent via SMS', {
          description: 'Check your messages for the OTP code'
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSMSFallback = async () => {
    setSendingFallback(true);
    try {
      const cleaned = phone.replace(/\s/g, '');
      await publicApi.sendOTPSMSFallback('+91' + cleaned);
      
      setChannel('sms');
      setCountdown(35);
      setCanShowFallback(false);
      
      toast.success('📱 OTP sent via SMS', {
        description: 'Check your messages for the OTP code'
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send SMS';
      toast.error(message);
    } finally {
      setSendingFallback(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Enter a valid 6-digit OTP');
      return;
    }

    setVerifying(true);
    try {
      const cleaned = phone.replace(/\s/g, '');
      await publicApi.verifyOTP('+91' + cleaned, otp);
      
      toast.success('✅ OTP Verified Successfully!');
      // Navigate to check-in or dashboard
      setTimeout(() => navigate('/checkin'), 1500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired OTP';
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-['EB_Garamond'] text-4xl font-bold text-[#F2F2F2] mb-2">
            WhatsApp-First OTP
          </h1>
          <p className="text-[#B9B9B9]">
            {otpSent ? 'Enter verification code' : 'Smart OTP delivery test'}
          </p>
        </div>

        <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          {!otpSent ? (
            /* Step 1: Phone Input */
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <Label htmlFor="phone" className="text-[#F2F2F2]">Mobile Number</Label>
                <div className="flex gap-2 mt-2">
                  <div className="flex items-center px-3 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.02)]">
                    <span className="text-[#B9B9B9]">+91</span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    className="flex-1"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || phone.length !== 10}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    Send OTP
                  </>
                )}
              </Button>

              <div className="mt-6 p-4 rounded-lg bg-[rgba(212,168,67,0.05)] border border-[rgba(212,168,67,0.2)]">
                <p className="text-xs text-[#B9B9B9] leading-relaxed">
                  <strong className="text-[#D4A843]">Smart Flow:</strong><br />
                  1. Checks if number is on WhatsApp<br />
                  2. Sends OTP via WhatsApp first<br />
                  3. Falls back to SMS if needed<br />
                  4. SMS fallback available after 35s
                </p>
              </div>
            </form>
          ) : (
            /* Step 2: OTP Verification */
            <div className="space-y-4">
              {/* Channel Info */}
              <div className="p-4 rounded-lg" style={{
                background: channel === 'whatsapp' ? 'rgba(37,211,102,0.1)' : 'rgba(212,168,67,0.1)',
                border: `1px solid ${channel === 'whatsapp' ? 'rgba(37,211,102,0.3)' : 'rgba(212,168,67,0.3)'}`
              }}>
                <div className="flex items-center gap-2 mb-2">
                  {channel === 'whatsapp' ? (
                    <MessageCircle size={20} style={{ color: '#25D366' }} />
                  ) : (
                    <Mail size={20} style={{ color: '#D4A843' }} />
                  )}
                  <span className="font-semibold" style={{ color: channel === 'whatsapp' ? '#25D366' : '#D4A843' }}>
                    OTP sent via {channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                  </span>
                  <CheckCircle2 size={16} style={{ color: channel === 'whatsapp' ? '#25D366' : '#D4A843' }} />
                </div>
                <p className="text-sm text-[#B9B9B9]">
                  {channel === 'whatsapp' 
                    ? 'Check your WhatsApp messages for the 6-digit code'
                    : 'Check your SMS inbox for the 6-digit code'}
                </p>
              </div>

              {/* OTP Input */}
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <Label htmlFor="otp" className="text-[#F2F2F2]">Enter OTP</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="mt-2 text-center text-2xl tracking-widest"
                    disabled={verifying}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={verifying || otp.length !== 6}
                >
                  {verifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>
              </form>

              {/* SMS Fallback */}
              {channel === 'whatsapp' && !canShowFallback && (
                <div className="text-center p-3 rounded-lg bg-[rgba(255,255,255,0.02)]">
                  <div className="flex items-center justify-center gap-2 text-[#B9B9B9]">
                    <Clock size={16} />
                    <span className="text-sm">
                      SMS fallback available in {countdown}s
                    </span>
                  </div>
                </div>
              )}

              {canShowFallback && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleSMSFallback}
                  disabled={sendingFallback}
                >
                  {sendingFallback ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending SMS...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Didn't receive? Send via SMS
                    </>
                  )}
                </Button>
              )}

              {/* Change Number */}
              <button
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                  setCountdown(35);
                  setCanShowFallback(false);
                }}
                className="w-full text-sm text-[#B9B9B9] hover:text-[#F2F2F2] transition-colors"
              >
                Change phone number
              </button>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/checkin')}
            className="text-sm text-[#B9B9B9] hover:text-[#F2F2F2] transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} />
            Back to Check-in
          </button>
        </div>
      </div>
    </div>
  );
}
